<template>
  <!-- The contract builder (lib/view/contracts/builder/, origin/main): the steps Document →
       Recipients → Fields → Review & send (contract_builder_screen.dart), "Draft saved" and "Send
       for signature"; Document's "Rich text template / Write it here", "Upload a PDF / Place fields
       on it" and "Fill in the details" with merge fields; Recipients' "Who signs", the signing
       order hint, Signer / Countersigner / CC rows, "Add by email" and "Add teammate"; Fields'
       palette (Signature, Initials, Text, Date, Name), "Placing for" and "Every signer has a
       signature field"; Review's "Before you send", Verification ("Email code"), Reminders and "On
       completion" ("Create an invoice from the linked proposal"). Strings are the app's
       (app_en.arb ctb*, contracts*); people and the document are sample data.
       States: document → recipients → fields → review; the server renders `review`. -->
  <div class="rp cbw" :class="`cbw--${current}`" aria-hidden="true">
    <header class="rp-head">
      <span class="cbw-title"><b translate="no">Service agreement</b><small>Draft saved</small></span>
      <span class="rp-btn">Send for signature</span>
    </header>
    <nav class="cbw-steps">
      <span v-for="(step, index) in steps" :key="step.key" :class="{ 'is-on': step.key === current, 'is-done': index < stepIndex }">
        <i class="rp-num">{{ index + 1 }}</i>{{ step.label }}
      </span>
    </nav>

    <div class="cbw-body">
      <!-- Document -->
      <section class="cbw-pane cbw-pane--document">
        <div class="cbw-kinds">
          <span class="cbw-kind is-on"><b>Rich text template</b><small>Write it here</small></span>
          <span class="cbw-kind"><b>Upload a PDF</b><small>Place fields on it</small></span>
        </div>
        <p class="cbw-label">Fill in the details</p>
        <p class="cbw-hint">Filled from the client where we can. Anything highlighted is missing on the client and prints blank.</p>
        <dl class="cbw-merge">
          <div v-for="row in merge" :key="row.label"><dt>{{ row.label }}</dt><dd :translate="row.sample ? 'no' : undefined" :class="{ 'is-soft': !row.sample }">{{ row.value }}</dd></div>
        </dl>
      </section>

      <!-- Recipients -->
      <section class="cbw-pane cbw-pane--recipients">
        <div class="cbw-row-head">
          <p class="cbw-label">Who signs</p>
          <span class="cbw-toggle"><span>Signing order</span><i class="cbw-switch" /></span>
        </div>
        <p class="cbw-hint">In this order. Each person gets the email when the one before them has signed.</p>
        <ol class="cbw-people">
          <li v-for="person in people" :key="person.name">
            <span class="cbw-order rp-num">{{ person.order }}</span>
            <span class="cbw-avatar" translate="no">{{ person.initials }}</span>
            <span class="cbw-who"><b translate="no">{{ person.name }}</b><small>{{ person.from }}</small></span>
            <em class="rp-chip" :class="person.tone">{{ person.role }}</em>
          </li>
        </ol>
        <span class="cbw-adds"><span class="rp-btn-quiet">Add by email</span><span class="rp-btn-quiet">Add teammate</span></span>
      </section>

      <!-- Fields -->
      <section class="cbw-pane cbw-pane--fields">
        <div class="cbw-fields">
          <div class="cbw-palette">
            <span class="cbw-placing">Placing for <b translate="no">Ana Lopez</b></span>
            <span v-for="field in palette" :key="field" class="cbw-tool" :class="{ 'is-hot': field === 'Signature' }">{{ field }}</span>
          </div>
          <div class="cbw-sheet">
            <small class="cbw-sheet-no">Page 3</small>
            <i v-for="n in 5" :key="n" class="cbw-line" :style="{ '--w': `${[94, 86, 90, 70, 52][n - 1]}%` }" />
            <span class="cbw-box cbw-box--client cbw-box--sig">Signature</span>
            <span class="cbw-box cbw-box--client cbw-box--date">Date</span>
            <span class="cbw-box cbw-box--team cbw-box--sig2">Signature</span>
          </div>
        </div>
        <span class="rp-chip rp-chip--green cbw-check"><svg viewBox="0 0 24 24"><path :d="check" /></svg>Every signer has a signature field</span>
      </section>

      <!-- Review & send -->
      <section class="cbw-pane cbw-pane--review">
        <p class="cbw-label">Before you send</p>
        <dl class="cbw-review">
          <div><dt>Recipients</dt><dd translate="no">Signers: Ana Lopez, then Jordan Lee</dd></div>
          <div><dt>Verification</dt><dd><span class="rp-chip rp-chip--blue">Email code</span></dd></div>
          <div><dt>Reminders</dt><dd class="rp-num">After 2 days, then every 3 days · max 3</dd></div>
          <div><dt>On completion</dt><dd><span class="cbw-tick"><svg viewBox="0 0 24 24"><path :d="check" /></svg></span>Create an invoice from the linked proposal</dd></div>
        </dl>
        <p class="cbw-hint cbw-hint--foot">Email code — the signer enters a code we email them before they can sign. Recommended for most contracts.</p>
      </section>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  state: { type: String, default: "review" },
});

const steps = [
  { key: "document", label: "Document" },
  { key: "recipients", label: "Recipients" },
  { key: "fields", label: "Fields" },
  { key: "review", label: "Review & send" },
];
const current = computed(() => (steps.some((step) => step.key === props.state) ? props.state : "review"));
const stepIndex = computed(() => steps.findIndex((step) => step.key === current.value));
const merge = [
  { label: "Client name", value: "Ana Lopez", sample: true },
  { label: "Company name", value: "Northwind Studio", sample: true },
  { label: "Your name", value: "Jordan Lee", sample: true },
  { label: "Date sent", value: "Filled in when sent" },
];
const people = [
  { order: 1, initials: "AL", name: "Ana Lopez", from: "From client Northwind Studio", role: "Signer", tone: "rp-chip--blue" },
  { order: 2, initials: "JL", name: "Jordan Lee (you)", from: "Teammate", role: "Countersigner", tone: "" },
  { order: "", initials: "PS", name: "Priya Shah", from: "Gets the signed copy", role: "CC", tone: "" },
];
const palette = ["Signature", "Initials", "Text", "Date", "Name"];
const check = "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z";
</script>

<style scoped>
.cbw { position: relative; container-type: inline-size; }
.cbw-title { display: grid; min-width: 0; line-height: 1.25; }
.cbw-title small { color: var(--rp-muted); font-size: 11px; }
.cbw-steps { display: flex; flex: none; gap: 4px; overflow: hidden; border-bottom: 1px solid var(--rp-line); background: var(--rp-page); padding: 7px 10px; }
.cbw-steps span { position: relative; display: inline-flex; align-items: center; gap: 5px; padding: 4px 7px; color: var(--rp-muted); font-size: 11.5px; white-space: nowrap; isolation: isolate; }
.cbw-steps span::before { content: ""; position: absolute; inset: 0; z-index: -1; border-radius: 6px; background: var(--rp-hover); opacity: 0; transition: opacity 0.25s ease; }
.cbw-steps span.is-on::before { opacity: 1; }
.cbw-steps .is-on { color: var(--rp-text); font-weight: 600; }
.cbw-steps i { position: relative; display: grid; place-items: center; width: 17px; height: 17px; border: 1px solid var(--rp-line); border-radius: 5px; font-size: 10px; font-style: normal; }
.cbw-steps .is-done i { border-color: transparent; background: var(--rp-green-fill); color: var(--rp-green); }

.cbw-body { position: relative; display: grid; flex: 1; min-height: 0; overflow: hidden; }
.cbw-pane { grid-area: 1 / 1; display: grid; align-content: start; gap: 8px; padding: 12px 14px; opacity: 0; transform: translateY(8px); transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.33, 1, 0.68, 1); }
.cbw--document .cbw-pane--document,
.cbw--recipients .cbw-pane--recipients,
.cbw--fields .cbw-pane--fields,
.cbw--review .cbw-pane--review { opacity: 1; transform: none; }

.cbw-label { margin: 0; font-size: 12.5px; font-weight: 600; }
.cbw-hint { margin: -4px 0 0; color: var(--rp-muted); font-size: 11px; line-height: 1.4; }
.cbw-kinds { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 4px; }
.cbw-kind { display: grid; gap: 1px; border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); padding: 9px 10px; }
.cbw-kind.is-on { box-shadow: inset 0 0 0 1.5px var(--rp-ink); }
.cbw-kind b { font-size: 12px; font-weight: 600; }
.cbw-kind small { color: var(--rp-muted); font-size: 11px; }
.cbw-merge { display: grid; margin: 0; border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); }
.cbw-merge div { display: flex; justify-content: space-between; gap: 10px; padding: 7px 10px; font-size: 12px; }
.cbw-merge div + div { border-top: 1px solid var(--rp-line); }
.cbw-merge dt { color: var(--rp-muted); }
.cbw-merge dd { margin: 0; font-weight: 600; }
.cbw-merge dd.is-soft { color: var(--rp-muted); font-weight: 500; }

.cbw-row-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.cbw-toggle { display: inline-flex; align-items: center; gap: 7px; color: var(--rp-muted); font-size: 11.5px; }
.cbw-switch { position: relative; width: 28px; height: 16px; border-radius: 999px; background: var(--rp-ink); }
.cbw-switch::after { content: ""; position: absolute; top: 2px; right: 2px; width: 12px; height: 12px; border-radius: 50%; background: var(--rp-ink-text); }
.cbw-people { display: grid; margin: 0; border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); padding: 0 10px; list-style: none; }
.cbw-people li { display: flex; align-items: center; gap: 8px; padding: 8px 0; }
.cbw-people li + li { border-top: 1px solid var(--rp-line); }
.cbw-order { width: 12px; color: var(--rp-muted); font-size: 11px; text-align: center; }
.cbw-avatar { display: grid; flex: none; place-items: center; width: 26px; height: 26px; border-radius: 50%; background: var(--rp-grey-fill); color: var(--rp-muted); font-size: 10px; font-weight: 700; }
.cbw-who { display: grid; flex: 1; min-width: 0; line-height: 1.3; }
.cbw-who b { overflow: hidden; font-size: 12px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.cbw-who small { overflow: hidden; color: var(--rp-muted); font-size: 10.5px; text-overflow: ellipsis; white-space: nowrap; }
.cbw-adds { display: flex; gap: 8px; }

.cbw-fields { display: grid; grid-template-columns: 112px 1fr; gap: 10px; }
.cbw-palette { display: grid; align-content: start; gap: 5px; }
.cbw-placing { margin-bottom: 2px; color: var(--rp-muted); font-size: 10.5px; line-height: 1.3; }
.cbw-placing b { display: block; color: var(--rp-text); font-size: 11.5px; }
.cbw-tool { border: 1px solid var(--rp-line); border-radius: 6px; background: var(--rp-card); padding: 5px 8px; font-size: 11.5px; }
.cbw-tool.is-hot { border-color: var(--rp-blue); background: var(--rp-blue-fill); color: var(--rp-blue); font-weight: 600; }
.cbw-sheet { position: relative; display: grid; align-content: start; gap: 6px; height: 196px; border: 1px solid var(--rp-line); border-radius: 6px; background: #fff; padding: 10px 12px; }
.dark .cbw-sheet { background: #f4f4f2; }
.cbw-sheet-no { color: #6b7078; font-size: 9.5px; text-align: right; }
.cbw-line { display: block; width: var(--w); height: 5px; border-radius: 2px; background: #e3e5e8; }
.cbw-box { position: absolute; display: grid; place-items: center; border: 1.5px dashed; border-radius: 4px; font-size: 10px; font-weight: 600; }
.cbw-box--client { border-color: #3f58bf; background: rgb(63 88 191 / 0.1); color: #3f58bf; }
.cbw-box--team { border-color: #7948af; background: rgb(121 72 175 / 0.1); color: #7948af; }
.cbw-box--sig { left: 12px; bottom: 54px; width: 44%; height: 30px; }
.cbw-box--date { right: 12px; bottom: 54px; width: 32%; height: 30px; }
.cbw-box--sig2 { left: 12px; bottom: 12px; width: 44%; height: 30px; }
.cbw-pane--fields .cbw-box { opacity: 0; transform: scale(0.94); transition: opacity 0.3s ease, transform 0.35s cubic-bezier(0.33, 1, 0.68, 1); }
.cbw--fields .cbw-box, .cbw--review .cbw-box { opacity: 1; transform: none; }
.cbw--fields .cbw-box--date { transition-delay: 0.35s; }
.cbw--fields .cbw-box--sig2 { transition-delay: 0.7s; }
.cbw-check { justify-self: start; }

.cbw-review { display: grid; margin: 0; border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); }
.cbw-review div { display: grid; grid-template-columns: 96px 1fr; gap: 10px; align-items: center; padding: 8px 10px; font-size: 12px; }
.cbw-review div + div { border-top: 1px solid var(--rp-line); }
.cbw-review dt { color: var(--rp-muted); }
.cbw-review dd { display: flex; align-items: center; gap: 6px; margin: 0; font-weight: 500; }
.cbw-tick { display: grid; flex: none; place-items: center; width: 15px; height: 15px; border-radius: 4px; background: var(--rp-ink); color: var(--rp-ink-text); }
.cbw-tick svg { width: 11px; height: 11px; }
.cbw-hint--foot { margin: 0; }

@container (max-width: 420px) {
  .cbw-steps span:not(.is-on) { font-size: 0; gap: 0; }
  .cbw-steps span:not(.is-on) i { font-size: 10px; }
  .cbw-fields { grid-template-columns: 1fr; }
  .cbw-palette { display: flex; flex-wrap: wrap; gap: 5px; }
  .cbw-placing { width: 100%; }
  .cbw-review div { grid-template-columns: 1fr; gap: 2px; }
  .cbw-people li:last-child { display: none; }
  /* Phones: the body is about 330px, so the secondary hints go and the sheet is shorter. */
  .cbw-pane { gap: 7px; padding: 10px 12px; }
  .cbw-pane--recipients .cbw-hint, .cbw-hint--foot { display: none; }
  .cbw-sheet { height: 140px; padding: 8px 10px; }
  .cbw-line:nth-of-type(n + 4) { display: none; }
  .cbw-box--sig, .cbw-box--date { bottom: 46px; height: 26px; }
  .cbw-box--sig2 { bottom: 10px; height: 26px; }
}
</style>
